import { RouteRecordRaw } from 'vue-router'

import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import { PERFORMANCE_PARAMS } from '@/modules/performance/stores/PerformanceReservationStore'

import { UserGroup } from '@generated/types'

export const PERFORMANCE_ID_PARAM_REGEX = '[pc]_\\d+'
export const RESERVATION_ID_PARAM_REGEX = '[pc]r_\\d+'
function getPerformanceIdValue(performanceIdParam: string | string[]): number {
  const performanceIdParamString = Array.isArray(performanceIdParam)
    ? performanceIdParam[0]
    : performanceIdParam
  const [, value] = performanceIdParamString.split('_')
  if (isNaN(Number(value))) return PERFORMANCE_PARAMS.EMPTY
  return Number(value)
}

export const performanceRoutes: RouteRecordRaw[] = [
  {
    path: '/venue/performance/:performanceId',
    name: 'performanceDetail',
    redirect: { name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST },
  },
  {
    path: `/venue/performance-new/:performanceId(${PERFORMANCE_ID_PARAM_REGEX})`,
    component: () => import('@/modules/performance/layouts/PerformanceLayoutNew.vue'),
    props: route => ({ performanceId: route.params.performanceId }),
    meta: {
      requiresAuth: UserGroup.MANAGERS,
      pageTitle: 'Performance',
    },
    children: [
      { path: '', redirect: { name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST } },
      {
        path: 'schedule/',
        name: PERFORMANCE_ROUTE_NAMES.SCHEDULE,
        component: () => import('@/modules/performance/pages/PerformanceSchedule.vue'),
        meta: { pageTitle: 'Performance - Schedule' },
      },
      {
        path: 'tables/',
        children: [
          {
            path: '',
            name: PERFORMANCE_ROUTE_NAMES.TABLES,
            component: () => import('@/modules/performance/pages/PerformanceTableList.vue'),
            meta: { pageTitle: 'Performance - Table List' },
          },
          {
            path: ':tableIds+',
            name: PERFORMANCE_ROUTE_NAMES.TABLE_EDIT,
            component: () => import('@/modules/performance/pages/PerformanceTableEdit.vue'),
            props: route => {
              const tableIds: Array<number | string> = (route.params.tableIds as string[]).map(
                id => (isNaN(Number(id)) ? id : Number(id)),
              )
              return { tableIds }
            },
            meta: { pageTitle: 'Performance - Table Detail' },
          },
        ],
      },
      {
        path: 'seating-times/',
        name: PERFORMANCE_ROUTE_NAMES.SEATING_TIMES,
        component: () => import('@/modules/performance/pages/PerformanceSeatingTimes.vue'),
        meta: { pageTitle: 'Performance - Seating Times' },
      },
      {
        path: 'add-ons/',
        children: [
          {
            path: '',
            name: PERFORMANCE_ROUTE_NAMES.ADD_ONS,
            component: () => import('@/modules/performance/pages/PerformanceAddOns.vue'),
            meta: { pageTitle: 'Performance - Add-ons' },
          },
          {
            path: ':addOnId',
            name: PERFORMANCE_ROUTE_NAMES.ADD_ON_EDIT,
            component: () => import('@/modules/performance/pages/PerformanceAddOnEdit.vue'),
            props: route => ({
              addOnId: Number(route.params.addOnId),
            }),
            meta: { pageTitle: 'Performance - Add-on Detail' },
          },
        ],
      },
      {
        path: 'pricing-tiers/',
        name: PERFORMANCE_ROUTE_NAMES.PRICING_TIERS,
        component: () => import('@/modules/performance/pages/PerformancePricingTiers.vue'),
        meta: { pageTitle: 'Performance - Pricing Tiers' },
      },
      {
        path: 'dashboard/',
        name: PERFORMANCE_ROUTE_NAMES.DASHBOARD,
        component: () => import('@/modules/performance/pages/PerformanceDashboard.vue'),
        meta: { pageTitle: 'Performance - Dashboard' },
      },
      {
        path: 'livestream',
        name: PERFORMANCE_ROUTE_NAMES.LIVESTREAM,
        component: () => import('@/modules/performance/pages/PerformanceLivestream.vue'),
        meta: { pageTitle: 'Performance - Livestream' },
      },
      {
        path: 'miscellaneous',
        name: PERFORMANCE_ROUTE_NAMES.MISCELLANEOUS,
        component: () => import('@/modules/performance/pages/PerformanceMiscellaneous.vue'),
        meta: { pageTitle: 'Performance - Miscellaneous' },
      },
      {
        path: 'email-guests',
        name: PERFORMANCE_ROUTE_NAMES.EMAIL_GUESTS,
        component: () => import('@/modules/performance/pages/PerformanceEmailGuests.vue'),
        meta: { pageTitle: 'Performance - Email Guests' },
      },
      {
        path: 'reservations/',
        children: [
          {
            path: '',
            name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST,
            component: () => import('@/modules/performance/pages/PerformanceReservationList.vue'),
            meta: { pageTitle: 'Performance - Reservation List' },
          },
          {
            path: 'walk-in',
            name: PERFORMANCE_ROUTE_NAMES.RESERVATION_WALK_IN,
            component: () => import('@/modules/performance/pages/PerformanceReservationWalkIn.vue'),
            meta: { pageTitle: 'Performance - Reservation Walk-in' },
          },
          {
            path: 'add-ons',
            name: PERFORMANCE_ROUTE_NAMES.RESERVATION_ADD_ON_LIST,
            component: () =>
              import('@/modules/performance/pages/PerformanceReservationAddOnList.vue'),
            meta: { pageTitle: 'Performance - Reservation Add-ons' },
          },
          {
            path: `:reservationId(${RESERVATION_ID_PARAM_REGEX})`,
            children: [
              {
                path: '',
                name: PERFORMANCE_ROUTE_NAMES.RESERVATION_DETAIL,
                component: () =>
                  import('@/modules/performance/pages/PerformanceReservationDetail.vue'),
                props: route => ({
                  reservationId: getPerformanceIdValue(route.params.reservationId),
                  performanceId: getPerformanceIdValue(route.params.performanceId),
                }),
                meta: { pageTitle: 'Performance - Reservation Detail' },
              },
              {
                path: 'edit',
                name: PERFORMANCE_ROUTE_NAMES.RESERVATION_EDIT,
                component: () =>
                  import('@/modules/performance/pages/PerformanceReservationEdit.vue'),
                props: route => ({
                  reservationId: getPerformanceIdValue(route.params.reservationId),
                  performanceId: getPerformanceIdValue(route.params.performanceId),
                }),
                meta: { pageTitle: 'Performance - Reservation Edit' },
              },
              {
                path: 'refund',
                name: PERFORMANCE_ROUTE_NAMES.RESERVATION_REFUND,
                component: () =>
                  import('@/modules/performance/pages/PerformanceReservationRefund.vue'),
                props: route => ({
                  reservationId: getPerformanceIdValue(route.params.reservationId),
                  performanceId: getPerformanceIdValue(route.params.performanceId),
                }),
                meta: { pageTitle: 'Performance - Reservation Refund' },
              },
            ],
          },
        ],
      },
      {
        path: 'configuration/',
        name: PERFORMANCE_ROUTE_NAMES.CS_DATE_CONFIGURE,
        component: () => import('@/modules/performance/pages/PerformanceCSDateConfigure.vue'),
        meta: { pageTitle: 'CSDate - Configuration' },
      },
    ],
  },
  {
    path: `/venue/performance-new/:performanceId(${PERFORMANCE_ID_PARAM_REGEX})/print-reservations`,
    name: PERFORMANCE_ROUTE_NAMES.PRINT_RESERVATIONS,
    component: () => import('@/modules/performance/pages/PerformancePrintReservations.vue'),
    props: route => ({ performanceId: getPerformanceIdValue(route.params.performanceId) }),
    meta: { pageTitle: 'Performance - Print Reservations' },
  },
  {
    path: `/venue/performance-new/:performanceId(${PERFORMANCE_ID_PARAM_REGEX})/reservations/:reservationId(${RESERVATION_ID_PARAM_REGEX})/move-to-diff-perf`,
    name: PERFORMANCE_ROUTE_NAMES.RESERVATION_MOVE_DETAIL,
    component: () => import('@/modules/performance/pages/PerformanceMoveReservation.vue'),
    props: route => ({
      reservationId: getPerformanceIdValue(route.params.reservationId),
      performanceId: getPerformanceIdValue(route.params.performanceId),
    }),
    meta: { pageTitle: 'Moving Reservation' },
  },
  {
    path: '/venue/reservations/performance/:performanceId',
    name: 'reservationsPerformance',
    redirect: { name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST },
  },
  {
    path: '/venue/reservations',
    name: 'reservations',
    redirect: {
      name: PERFORMANCE_ROUTE_NAMES.RESERVATION_LIST,
      params: { performanceId: `p_${PERFORMANCE_PARAMS.EMPTY}` },
    },
  },
  {
    path: '/venue/performance-new/0/',
    redirect: '/venue/performance-new/p_0/reservations/',
  },
  {
    path: '/venue/performance-new/0/reservations/',
    redirect: '/venue/performance-new/p_0/reservations/',
  },
  {
    path: '/venue/performance-new/0/schedule/',
    redirect: '/venue/performance-new/p_0/schedule/',
  },
]
